/**
 * StatusDropdown — Lovable-style DEAL STATUS picker for the Pipeline table
 * AND the cockpit PipelineSummary table.
 *
 * Renders the current status as a clickable pill (reuses StatusBadge styling)
 * with a ▾ caret that opens a positioned panel listing all 6 pipeline statuses:
 *   - 5 cycle statuses from PIPELINE_STATUS_ORDER (with step indicators)
 *   - 1 "Passed" side-action row (muted, below a hairline divider)
 *
 * On non-current row click: fires useCyclePipelineStatus with targetStatus
 * (optimistic + server PUT /api/conversations/:id/status) then closes.
 * Current row: marked CURRENT, not clickable.
 * Escape / click-outside / scroll / resize: close.
 *
 * PORTAL RENDERING: The dropdown panel is mounted on document.body via
 * createPortal with position: fixed + zIndex: 9999. This escapes all
 * Panel overflow-hidden / overflow-y-auto / max-h scroll containers that
 * would clip the panel in both the /pipeline DealTable and cockpit
 * PipelineSummary. Position is computed from the trigger's
 * getBoundingClientRect() each time it opens; a useLayoutEffect measures
 * the mounted panel height to flip above the trigger when it would overflow
 * the viewport bottom edge. Left-edge guard flips to right-align when the
 * panel would overflow the right viewport edge.
 *
 * Color rule: alpha-concat sites use PIPELINE_STATUS_HEX (hex values).
 * Single-token text/fill uses PIPELINE_STATUS_COLORS (var() form).
 * NO raw hex literals — all hex comes from the typed PIPELINE_STATUS_HEX map.
 * NO Tailwind class interpolation — all class names are complete static literals.
 *
 * DEAL STATUS dropdown — 2026-05-18. Portal fix — 2026-05-18.
 */
import {
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import type { PipelineStatus } from '@arcanum-hq/shared-types';
import { PIPELINE_STATUS_ORDER } from '@arcanum-hq/shared-types';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { useCyclePipelineStatus } from '@/hooks/use-cycle-pipeline-status';
import { PIPELINE_STATUS_HEX, PIPELINE_STATUS_COLORS } from '@/types';

// ---------------------------------------------------------------------------
// Static label-class lookups (complete class strings — no interpolation)
// ---------------------------------------------------------------------------

/** Text class for a status row label, keyed by relationship to current. */
const LABEL_CLASS: Record<'current' | 'past' | 'future' | 'passed', string> = {
  current: 'text-[9px] font-mono font-semibold text-text-bright',
  past: 'text-[9px] font-mono text-text-dim',
  future: 'text-[9px] font-mono text-text-muted',
  passed: 'text-[9px] font-mono text-text-dim',
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface StatusDropdownProps {
  conversationId: string;
  status: PipelineStatus;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function StatusDropdown({
  conversationId,
  status,
}: StatusDropdownProps) {
  // Guard: no conversationId → render static badge (no interactivity)
  if (!conversationId) {
    return <StatusBadge status={status} />;
  }

  return (
    <StatusDropdownInner conversationId={conversationId} status={status} />
  );
}

// ---------------------------------------------------------------------------
// Panel position state
// ---------------------------------------------------------------------------

interface PanelPos {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
}

// ---------------------------------------------------------------------------
// Inner implementation — only rendered when conversationId is truthy.
// ---------------------------------------------------------------------------

function StatusDropdownInner({
  conversationId,
  status,
}: {
  conversationId: string;
  status: PipelineStatus;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // panelPos holds the computed fixed-position style for the portaled panel.
  // Starts as an empty object; populated on open from the trigger's
  // getBoundingClientRect(), then refined by useLayoutEffect once the panel
  // is mounted and its actual height is known.
  const [panelPos, setPanelPos] = useState<PanelPos>({});
  const { mutate } = useCyclePipelineStatus();

  const close = useCallback(() => setOpen(false), []);

  // ── Close listeners (attached only while open) ───────────────────────────

  // Close on click-outside (pointerdown — fires before click).
  // The panel is portaled to document.body, so DOM containment must check
  // BOTH the triggerRef AND the panelRef (not just the wrapper cell element).
  useEffect(() => {
    if (!open) return;

    function onPointerDown(e: PointerEvent) {
      const target = e.target as Node | null;
      if (
        triggerRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return; // click within trigger or portaled panel — keep open
      }
      close();
    }

    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, close]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        close();
        triggerRef.current?.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, close]);

  // Close on any scroll (capture phase — matches Lovable reference).
  // Prevents a mis-positioned panel after the trigger has scrolled away.
  useEffect(() => {
    if (!open) return;
    window.addEventListener('scroll', close, true);
    return () => window.removeEventListener('scroll', close, true);
  }, [open, close]);

  // Close on window resize (panel position would be stale).
  useEffect(() => {
    if (!open) return;
    window.addEventListener('resize', close);
    return () => window.removeEventListener('resize', close);
  }, [open, close]);

  // ── Portal panel positioning ─────────────────────────────────────────────

  // Step 1: On open, compute an initial position from the trigger rect.
  // This runs synchronously before the portal mounts so the initial render
  // is already positioned (no layout flash).
  const PANEL_W = 168; // min-w matches CSS

  function computeInitialPos(): PanelPos {
    const trigger = triggerRef.current;
    if (!trigger) return {};
    const rect = trigger.getBoundingClientRect();
    // Default: below trigger, left-aligned
    const top: number | undefined = rect.bottom + 4;
    const bottom: number | undefined = undefined;
    let left: number | undefined = rect.left;
    let right: number | undefined = undefined;

    // Horizontal edge guard: right-align when panel overflows viewport right
    if (rect.left + PANEL_W > window.innerWidth - 4) {
      left = undefined;
      right = window.innerWidth - rect.right;
    }

    return { top, bottom, left, right };
  }

  // Step 2: After the panel mounts, measure its actual rendered height and
  // flip above the trigger if it would overflow the viewport bottom edge.
  // useLayoutEffect runs synchronously after DOM mutation — before the
  // browser paints — so the correction is invisible to the user.
  useLayoutEffect(() => {
    if (!open || !panelRef.current) return;
    const panel = panelRef.current;
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const panelHeight = panel.getBoundingClientRect().height;

    const wouldOverflowBottom =
      rect.bottom + 4 + panelHeight > window.innerHeight - 4;

    if (wouldOverflowBottom) {
      // Flip above: pin panel's bottom edge to trigger's top
      setPanelPos((prev) => ({
        ...prev,
        top: undefined,
        bottom: window.innerHeight - rect.top + 4,
      }));
    }
  }, [open]);

  // ── Open handler ─────────────────────────────────────────────────────────

  function handleOpen(e: React.MouseEvent) {
    e.stopPropagation(); // prevent row select/scroll
    if (!open) {
      setPanelPos(computeInitialPos());
    }
    setOpen((v) => !v);
  }

  // ── Row click ────────────────────────────────────────────────────────────

  const currentIndex = PIPELINE_STATUS_ORDER.indexOf(status);
  // "Passed" is not in the cycle order — currentIndex === -1 for Passed deals.
  const isPassed = status === 'Passed';

  function handleRowClick(picked: PipelineStatus) {
    if (picked === status) return; // current — no-op
    mutate({ conversationId, currentStatus: status, targetStatus: picked });
    close();
  }

  const colorVar = PIPELINE_STATUS_COLORS[status];

  // ── Portal panel style (position: fixed + zIndex inline) ─────────────────
  // zIndex is inline (not a Tailwind arbitrary class) — avoids the class-
  // extraction footgun and is the clean precedent for portaled fixed nodes.

  const portalStyle: React.CSSProperties = {
    position: 'fixed',
    minWidth: PANEL_W,
    zIndex: 9999,
    ...(panelPos.top !== undefined ? { top: panelPos.top } : {}),
    ...(panelPos.bottom !== undefined ? { bottom: panelPos.bottom } : {}),
    ...(panelPos.left !== undefined ? { left: panelPos.left } : {}),
    ...(panelPos.right !== undefined ? { right: panelPos.right } : {}),
  };

  // ── Portaled panel JSX ────────────────────────────────────────────────────

  const dropdownPanel = open
    ? createPortal(
        <div
          ref={panelRef}
          role="listbox"
          aria-label="Deal status"
          style={portalStyle}
          className="bg-panel-bg border border-panel-border rounded-[2px] shadow-[0_4px_24px_rgba(0,0,0,0.5)]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header band */}
          <div className="px-2.5 pt-1 pb-1.5 border-b border-panel-border">
            <span className="text-[7px] font-mono text-text-dim tracking-[1px] uppercase">
              Deal Status
            </span>
          </div>

          {/* Cycle status rows */}
          {PIPELINE_STATUS_ORDER.map((s, idx) => {
            const isCurrent = s === status;
            const rowHex = PIPELINE_STATUS_HEX[s];
            const rowColorVar = PIPELINE_STATUS_COLORS[s];

            // Indicator state relative to current position in cycle
            // When viewing a Passed deal, all cycle statuses are "future"
            let indicatorKind: 'check' | 'dot' | 'number';
            let labelKind: 'current' | 'past' | 'future';
            if (isCurrent) {
              indicatorKind = 'dot';
              labelKind = 'current';
            } else if (isPassed || idx > currentIndex) {
              indicatorKind = 'number';
              labelKind = 'future';
            } else {
              // idx < currentIndex
              indicatorKind = 'check';
              labelKind = 'past';
            }

            return (
              <div
                key={s}
                role="option"
                aria-selected={isCurrent}
                className={clsx(
                  'flex items-center gap-2 px-2.5 py-1.5 transition-colors duration-150',
                  isCurrent ? 'cursor-default' : 'cursor-pointer'
                )}
                style={{
                  background: isCurrent ? `${rowHex}14` : undefined,
                }}
                onMouseEnter={(e) => {
                  if (!isCurrent) {
                    (
                      e.currentTarget as HTMLDivElement
                    ).style.background = `${rowHex}0c`;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isCurrent) {
                    (e.currentTarget as HTMLDivElement).style.background = '';
                  }
                }}
                onClick={() => handleRowClick(s)}
              >
                {/* Left indicator — 14×14 circle */}
                <div
                  className="flex-shrink-0 w-3.5 h-3.5 rounded-full flex items-center justify-center"
                  style={{
                    background:
                      indicatorKind === 'dot' ? rowHex : 'transparent',
                    border:
                      indicatorKind !== 'dot'
                        ? `1px solid ${rowHex}66`
                        : 'none',
                  }}
                >
                  {indicatorKind === 'check' && (
                    <span
                      className="text-[7px] font-mono leading-none"
                      style={{ color: `${rowHex}99` }}
                    >
                      ✓
                    </span>
                  )}
                  {indicatorKind === 'number' && (
                    <span
                      className="text-[7px] font-mono leading-none tabular-nums"
                      style={{ color: `${rowHex}99` }}
                    >
                      {idx + 1}
                    </span>
                  )}
                  {/* dot: filled circle bg — no inner content */}
                </div>

                {/* Label */}
                <span className={clsx('flex-1', LABEL_CLASS[labelKind])}>
                  {s}
                </span>

                {/* Right affordance */}
                {isCurrent ? (
                  <span
                    className="text-[7px] font-mono tracking-[0.5px] uppercase"
                    style={{ color: rowColorVar }}
                  >
                    Current
                  </span>
                ) : (
                  <span
                    className="text-[10px] font-mono leading-none"
                    style={{ color: rowColorVar, opacity: 0.6 }}
                  >
                    →
                  </span>
                )}
              </div>
            );
          })}

          {/* Hairline divider before Passed side-action */}
          <div className="border-t border-panel-border mx-2.5" />

          {/* Passed side-action row */}
          {(() => {
            const passedHex = PIPELINE_STATUS_HEX['Passed'];
            const passedColorVar = PIPELINE_STATUS_COLORS['Passed'];
            const isCurrentPassed = status === 'Passed';

            return (
              <div
                role="option"
                aria-selected={isCurrentPassed}
                className={clsx(
                  'flex items-center gap-2 px-2.5 py-1.5 transition-colors duration-150',
                  isCurrentPassed ? 'cursor-default' : 'cursor-pointer'
                )}
                style={{
                  background: isCurrentPassed ? `${passedHex}14` : undefined,
                }}
                onMouseEnter={(e) => {
                  if (!isCurrentPassed) {
                    (
                      e.currentTarget as HTMLDivElement
                    ).style.background = `${passedHex}0c`;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isCurrentPassed) {
                    (e.currentTarget as HTMLDivElement).style.background = '';
                  }
                }}
                onClick={() => handleRowClick('Passed')}
              >
                {/* Neutral muted dot for Passed */}
                <div
                  className="flex-shrink-0 w-3.5 h-3.5 rounded-full flex items-center justify-center"
                  style={{
                    background: isCurrentPassed ? passedHex : 'transparent',
                    border: isCurrentPassed
                      ? 'none'
                      : `1px solid ${passedHex}66`,
                  }}
                >
                  {!isCurrentPassed && (
                    <span
                      className="text-[7px] font-mono leading-none"
                      style={{ color: `${passedHex}99` }}
                    >
                      —
                    </span>
                  )}
                </div>

                {/* Label */}
                <span className={clsx('flex-1', LABEL_CLASS['passed'])}>
                  Passed
                </span>

                {/* Right affordance */}
                {isCurrentPassed ? (
                  <span
                    className="text-[7px] font-mono tracking-[0.5px] uppercase"
                    style={{ color: passedColorVar }}
                  >
                    Current
                  </span>
                ) : (
                  <span
                    className="text-[10px] font-mono leading-none"
                    style={{ color: passedColorVar, opacity: 0.6 }}
                  >
                    →
                  </span>
                )}
              </div>
            );
          })()}
        </div>,
        document.body
      )
    : null;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Trigger — StatusBadge look + ▾ caret */}
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex items-center gap-1 cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent focus-visible:ring-offset-1 focus-visible:ring-offset-void rounded-[2px]"
        onClick={handleOpen}
      >
        <StatusBadge status={status} />
        <span
          className="text-[8px] leading-none"
          style={{ color: colorVar, opacity: 0.7 }}
          aria-hidden="true"
        >
          ▾
        </span>
      </button>

      {/* Portaled panel — mounted on document.body, position: fixed */}
      {dropdownPanel}
    </>
  );
}
